import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PolicyCar } from '@models/policy-car';
import { RoadAssistanceContextState } from '@modules/road-assistance/models/road-assistance';
import { VehicleChangeLoadingId, VehicleVersion } from '@modules/vehicle-data/models/vehicle-change';
import { spinner } from '@utils/rxjs';
import { joinStrings } from '@utils/utils';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PHONE_NUMBERS } from '../../../modules/receipt-payment/receipt-payment-constants';
import { AuthService } from '../core/auth/auth.service';
import { BaseService } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class RoadAssistanceService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private auth: AuthService,
    private mmobileHelper: MMobileHelperService,
    private or: OperationRegisterService
  ) { }

  validateProceedings(body, policy: PolicyCar) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/travelAssistance/validation`;
    const headers = this.base.getPrivateOIMHeaders();
    const params = this.or.getRoadAssistanceUrlQueryParams(policy);

    return this.http.post(url, body, { ...headers, params });
  }

  sendRoadAssistanceOnline(body, policy: PolicyCar) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/travelAssistance/creation`;
    const headers = this.base.getPrivateOIMHeaders();
    const params = this.or.getRoadAssistanceUrlQueryParams(policy);

    return this.http.post(url, body, { ...headers, params });
  }

  getRoadAssistancePhoneNumbers(): string[] {
    return this.mmobileHelper.getCustomConfigData(
      PHONE_NUMBERS,
      'roadAssistancePhoneNumbersList'
    ) || [];
  }

  getConcatenatedPhoneNumbers(phoneNumbers: string[]) {
    return joinStrings(phoneNumbers, ' / ');
  }

  sendForm(state: Partial<RoadAssistanceContextState>) {
    const url = `${this.base.getPublicBaseUrl()}/contact/callMeNow`;
    const policyNumberQuery = state.policySelected.policyNumber
      ? `P=${state.policySelected.policyNumber.trim()}`
      : 'P=';
    const free1Text = state.policySelected.frameNumber
      ? `M=${state.policySelected.frameNumber.trim()}`
      : policyNumberQuery;
    const mapUrl = `https://www.google.com/maps?q=${state.assistanceAddress?.lat},${state.assistanceAddress?.lng}`;

    const remarksText = `Cliente solicita asistencia en carretera \n Póliza/Matrícula:${'M=' +
      (state.policySelected.frameNumber ? state.policySelected.frameNumber.trim() : '') +
      ', P=' +
      (state.policySelected.policyNumber ? state.policySelected.policyNumber.trim() : '')
      }\n Ubicación: ${state.assistanceAddress?.rawAddress.trim()}`;
    let isClient = false;

    if (this.auth.getState().isLogged) {
      isClient = true;
    }
    const params = this.or.setRoadAssistanceParams(
      state.policySelected.frameNumber,
      state.assistanceData.contactData.documentNumber
    );
    const body = {
      name: state.assistanceData.contactData.name.trim(),
      email: '',
      postalCode: '',
      remarks: remarksText,
      client: isClient,
      phone: state.assistanceData.contactData.phone,
      priority: '',
      rule: '',
      campaign: 'CallMe_Asist_Movil',
      platform: '',
      free1: free1Text,
      free2: state.assistanceAddress?.rawAddress?.trim(),
      free3: `(${state.assistanceAddress?.lat},${state.assistanceAddress?.lng})`,
      free5: mapUrl,
      agents: '0'
    };

    return this.http.post(url, body, {
      ...this.base.getPublicHeaders(),
      params
    });
  }

  getVehiclesByFrameNumber(policy: PolicyCar): Observable<{ cars: Array<VehicleVersion> }> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carChange/vehicles`;
    const key = 'carPlate';
    const value = policy.frameNumber;
    const params = new HttpParams().set(key, value);
    const headers = this.base.getPrivateOIMHeaders();
    const emptyVehiclesResponse = { cars: [] };
    return this.http.get<{ cars: Array<VehicleVersion> }>(url, { ...headers, params })
      .pipe(
        map((data) =>  data ? data : emptyVehiclesResponse),
        spinner(VehicleChangeLoadingId.VEHICLE_VERSION),
        catchError((err) => of(emptyVehiclesResponse))
      );
  }
}
